import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import { NavBar, I18n, Navigation, Utils, Container, Panel, IconButton } from '../components';
import { COLOR, FONT, THEME, __WEB__ } from '../../constants/styles';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentEmployer, getCurrentEmployerPermissions } from '../../selectors/employers';
import ScheduleProvidersMenu from '../airforce/setup/appt_hours';
import OccHealthMasterRequirements from '../airforce/occ_health/master_requirements';
import OccHealthDepartments from '../airforce/occ_health/departments';
import VARegions from '../medRecruiter/setup/regions';
import InviteRequests from '../airforce/invite_requests';
import ClinicalLocations from '../airforce/setup/clinical_locations';
import MedicalServices from '../airforce/setup/medical_services';
import Squadrons from '../airforce/setup/squadrons';
import { getSetupData } from './actions';
import {
  getOccHealthRequirements,
  getOccHealthDepartments,
  getOccHealthMasterDepartments,
  getMedRecruiterClinicalLocations,
} from './actions';
import { resetStatus } from '../employer/actions';
import { resetState as resetStaffStatus } from '../staff/actions';
import {
  BASE_SETUP_SQ_MED_SVC_CLINICAL_LOC,
  BASE_SETUP_OCC_HEALTH,
  STAFF_SCHEDULING,
  BASE_SETUP_NOTIFICATIONS,
  BASE_SETUP_SCHEDULE_HOURS_CMR,
  BASE_SETUP_SCHEDULE_HOURS_FLY_CMR,
} from '../../constants/permissions';
import { canView, canEdit } from '../../helpers/permissions';
import { REQUESTING, SUCCESS } from '../../actions/statusTypes';
import { SETUP_TAB } from '../../constants/tabs';
import { EMPLOYER_TYPE_MED_RECRUITER } from '../../constants/employerTypes';
import SchedulerNotificationMessages from '../airforce/setup/scheduler_notification_messages';
import SchedulerAppointmentWindows from '../airforce/setup/scheduler_appointment_windows';
import CMRRevenueCycle from '../airforce/setup/cmr_revenue_cycle';
import TopBarSetup from '../../navbar_maintab_setup';

const CMR_REVENUE_CYCLE_PERMISSIONS = [
  BASE_SETUP_SCHEDULE_HOURS_CMR,
  BASE_SETUP_SCHEDULE_HOURS_FLY_CMR,
];

const DATA_TO_FETCH = {
  medicalServices: ['medicalServices', 'clinicalLocationMedicalService'],
  clinicalLocations: ['clinicalLocations', 'clinicalLocationMedicalService'],
  clinicalLocationMedicalService: ['clinicalLocationMedicalService'],
  groups: ['groups'],
};

const BaseSetup = (props) => {
  const dispatch = useDispatch();
  const employer = useSelector(getCurrentEmployer);
  const permissions = useSelector(getCurrentEmployerPermissions);
  const coldStartFinished = useSelector((state) => state.application.coldStartFinished);
  const saveSetupDataStatus = useSelector((state) => state.screens.employer.saveSetupDataStatus);
  const getSetupDataStatus = useSelector((state) => state.screens.employer.getSetupDataStatus);
  const setupDataUpdated = useSelector((state) => state.screens.employer.setupDataUpdated);
  const getMedRecruiterClinicalLocationsStatus = useSelector(
    (state) => state.screens.employer.getMedRecruiterClinicalLocationsStatus
  );
  const fetchInviteRequestsStatus = useSelector(
    (state) => state.screens.staff.fetchInviteRequestsStatus
  );
  const fetchInviteRequestsError = useSelector(
    (state) => state.screens.staff.fetchInviteRequestsError
  );
  const staffInviteStatus = useSelector((state) => state.screens.staff.inviteStatus);
  const staffInviteError = useSelector((state) => state.screens.staff.inviteError);
  const inviteRequestStatus = useSelector((state) => state.screens.employer.inviteRequestStatus);
  const inviteRequestError = useSelector((state) => state.screens.employer.inviteRequestError);

  const [screenVisible, setScreenVisible] = useState(false);
  const [confirmationOpts, setConfirmationOpts] = useState(null);

  const employerId = employer?.employerId;
  const employerType = employer?.uiConfig?.type;
  const isMedRecruiter = employerType === EMPLOYER_TYPE_MED_RECRUITER;
  const canViewSqMedsvcClinicalLoc = canView(permissions, BASE_SETUP_SQ_MED_SVC_CLINICAL_LOC);
  const canEditSqMedsvcClinicalLoc = canEdit(permissions, BASE_SETUP_SQ_MED_SVC_CLINICAL_LOC);
  const canViewOccHealth = canView(permissions, BASE_SETUP_OCC_HEALTH);
  const canViewVALocations = canView(permissions, STAFF_SCHEDULING);
  const canEditVALocations = canEdit(permissions, STAFF_SCHEDULING);
  const canViewNotifications = canView(permissions, BASE_SETUP_NOTIFICATIONS);
  const canViewCMRRevenueCycle = canView(permissions, CMR_REVENUE_CYCLE_PERMISSIONS);
  const canEditCMRRevenueCycle = canEdit(permissions, CMR_REVENUE_CYCLE_PERMISSIONS);
  const isBusy =
    saveSetupDataStatus === REQUESTING ||
    getSetupDataStatus === REQUESTING ||
    getMedRecruiterClinicalLocationsStatus === REQUESTING ||
    fetchInviteRequestsStatus === REQUESTING ||
    staffInviteStatus === REQUESTING ||
    inviteRequestStatus === REQUESTING;

  const successMessage =
    staffInviteStatus === SUCCESS || inviteRequestStatus === SUCCESS ? 'Invite Sent!' : null;
  const errorMessage =
    fetchInviteRequestsError?.message ||
    staffInviteError?.message ||
    inviteRequestError?.message ||
    null;

  useEffect(
    NavBar.setup(props, {
      listeners: {
        componentDidAppear: (id) => {
          Navigation.trackActiveTabNum(SETUP_TAB);
          setScreenVisible(true);
        },
        componentDidDisappear: (id) => {
          setScreenVisible(false);
        },
        handleDeepLink: ({ link, payload }) => {
          if (link === SETUP_TAB || link === 'setup/main') {
            Navigation.switchToTab(SETUP_TAB, props.componentId);
          }
        },
      },
    }),
    []
  );

  const fetchData = useCallback(() => {
    if (!screenVisible || !employerType || !employerId) return;

    if (__WEB__) {
      if (isMedRecruiter) {
        dispatch(getMedRecruiterClinicalLocations(employerId));
      } else {
        dispatch(getSetupData(employerId));
        dispatch(getOccHealthMasterDepartments());
        dispatch(getOccHealthRequirements(employerId));
        dispatch(getOccHealthDepartments(employerId));
      }
    }
  }, [dispatch, employerId, employerType, isMedRecruiter, screenVisible]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (__WEB__ && screenVisible && employerId && setupDataUpdated) {
      const dataToFetch = DATA_TO_FETCH[setupDataUpdated];

      if (dataToFetch) {
        dispatch(getSetupData(employerId, dataToFetch));
      }
    }
  }, [dispatch, employerId, screenVisible, setupDataUpdated]);

  const closeConfirmation = useCallback(() => {
    setConfirmationOpts(null);
  }, []);

  const handleConfirmationOpts = useCallback(
    (nextConfirmationOpts) => {
      const { onConfirmed } = nextConfirmationOpts;

      setConfirmationOpts({
        ...nextConfirmationOpts,
        onConfirmed: () => {
          onConfirmed?.();
          closeConfirmation();
        },
        onCancelled: closeConfirmation,
      });
    },
    [closeConfirmation]
  );

  const handleDissmissSuccess = useCallback(() => {
    dispatch(resetStatus());
    dispatch(resetStaffStatus());
  }, [dispatch]);

  const renderHeader = () => {
    const employerLogo = Utils.employerImagePath(employerId);

    return (
      <View style={styles.header} testID="setup-header">
        <Image source={{ uri: employerLogo }} style={styles.logoImage} />
        <View>
          <Text style={styles.headerText}>{employer?.name}</Text>
        </View>
      </View>
    );
  };

  const renderScheduleHours = () => {
    if (!__WEB__) return null;
    return (
      <Panel title="Schedule Hours" containerStyle={styles.flexPanel}>
        <View style={styles.topRowContainer}>
          <ScheduleProvidersMenu permissions={permissions} employer={employer} />
        </View>
      </Panel>
    );
  };

  const renderSchedulerAppointmentWindows = () => {
    if (!__WEB__ || !employerId) return null;

    return (
      <SchedulerAppointmentWindows
        employer={employer}
        permissions={permissions}
        onConfirmationOpts={handleConfirmationOpts}
        containerStyle={styles.flexPanel}
      />
    );
  };

  const renderCMRRevenueCycle = () => {
    if (!__WEB__ || !employerId || !canViewCMRRevenueCycle) return null;

    return (
      <CMRRevenueCycle
        employer={employer}
        canEdit={canEditCMRRevenueCycle}
        onConfirmationOpts={handleConfirmationOpts}
        containerStyle={styles.flexPanel}
      />
    );
  };

  const renderNotificationMessages = () => {
    if (!__WEB__ || !employerId || !canViewNotifications) return null;

    return (
      <SchedulerNotificationMessages
        employer={employer}
        permissions={permissions}
        onConfirmationOpts={handleConfirmationOpts}
      />
    );
  };

  const renderSquadrons = () => {
    if (!__WEB__ || !canViewSqMedsvcClinicalLoc) return null;

    return (
      <Squadrons
        collapsable={!__WEB__}
        containerStyle={styles.wideFlexPanel}
        onConfirmationOpts={handleConfirmationOpts}
        canEdit={canEditSqMedsvcClinicalLoc}
      />
    );
  };

  const renderMedicalServices = () => {
    if (!__WEB__ || !canViewSqMedsvcClinicalLoc) return null;

    return (
      <MedicalServices
        collapsable={!__WEB__}
        containerStyle={styles.flexPanel}
        onConfirmationOpts={handleConfirmationOpts}
        canEdit={canEditSqMedsvcClinicalLoc}
      />
    );
  };

  const renderClinicalLocations = () => {
    if (!__WEB__ || !canViewSqMedsvcClinicalLoc) return null;

    return (
      <ClinicalLocations
        collapsable={!__WEB__}
        containerStyle={styles.wideFlexPanel}
        onConfirmationOpts={handleConfirmationOpts}
        canEdit={canEditSqMedsvcClinicalLoc}
      />
    );
  };

  const renderVALocations = () => {
    if (!__WEB__ || !canViewVALocations) return null;

    return (
      <VARegions
        collapsable={!__WEB__}
        containerStyle={styles.wideFlexPanel}
        onConfirmationOpts={handleConfirmationOpts}
        canEdit={canEditVALocations}
      />
    );
  };

  const renderOccHealthDepartments = () => {
    if (!__WEB__ || !canViewOccHealth || !employerId) return null;

    return (
      <OccHealthDepartments onConfirmationOpts={handleConfirmationOpts} employerId={employerId} />
    );
  };

  const renderOccHealthMasterRequirements = () => {
    if (!__WEB__ || !canViewOccHealth) return null;

    return (
      <Panel
        title={'Occupational Health Master Requirements List'}
        collapsable={!__WEB__}
        containerStyle={styles.widePanel}
      >
        <OccHealthMasterRequirements onConfirmationOpts={handleConfirmationOpts} />
      </Panel>
    );
  };

  const renderInviteRequests = () => {
    if (!screenVisible) return null;
    return (
      <Panel
        title={'Requests for Outpatient App Invite'}
        collapsable={false}
        containerStyle={styles.widePanel}
      >
        <InviteRequests onConfirmationOpts={handleConfirmationOpts} />
      </Panel>
    );
  };

  const renderEmployer = () => {
    if (!employerType) return null;

    if (isMedRecruiter) {
      return (
        <View style={styles.contentContainer}>
          <View style={styles.row}>{renderVALocations()}</View>
          {renderInviteRequests()}
        </View>
      );
    }

    return (
      <View style={styles.contentContainer}>
        <View style={styles.row}>
          {renderScheduleHours()}
          {renderSquadrons()}
        </View>
        {renderCMRRevenueCycle()}
        {renderSchedulerAppointmentWindows()}
        <View style={styles.row}>
          {renderMedicalServices()}
          {renderClinicalLocations()}
        </View>
        {renderOccHealthDepartments()}
        {renderOccHealthMasterRequirements()}
        {renderNotificationMessages()}
        {renderInviteRequests()}
      </View>
    );
  };

  if (!coldStartFinished) return null;

  function renderSetup() {
    return (
      <Container
        confirmationOpts={confirmationOpts}
        scrollView={true}
        renderActivityOverlay={isBusy}
        successMessage={successMessage}
        errorMessage={errorMessage}
        onDismissMessage={handleDissmissSuccess}
      >
        {renderHeader()}
        {renderEmployer()}
      </Container>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <TopBarSetup componentId={props.componentId} />
      {renderSetup()}
    </View>
  );
};

BaseSetup.navigatorStyle = NavBar.navigatorStyleRootScreen;

const styles = StyleSheet.create({
  contentContainer: {
    padding: __WEB__ ? 20 : 0,
    paddingTop: __WEB__ ? 20 : 30,
  },
  flexPanel: __WEB__ ? { flex: 1 } : null,
  row: __WEB__ ? { flexDirection: 'row', alignItems: 'flex-start' } : {},
  wideFlexPanel: __WEB__ ? { flex: 1, minWidth: 500 } : null,
  widePanel: __WEB__ ? { minWidth: 820 } : null,
  topRowContainer: __WEB__ ? { height: 360 } : {},
  header: {
    ...THEME.basicRow,
    backgroundColor: COLOR.altContrast,
    borderBottomWidth: 1.5,
    borderColor: COLOR.altAccent,
    paddingVertical: 20,
    paddingHorizontal: 25,
  },
  logoImage: {
    width: 60,
    height: 60,
    // borderWidth: 1,
    // borderColor: COLOR.contrast,
    marginHorizontal: 10,
  },
  headerText: {
    ...FONT.light,
    fontSize: 24,
    color: COLOR.text,
  },
});

export default BaseSetup;
